/* eslint-disable prettier/prettier */
// WARNING: prettier is intentionally disabled to ensure code formatting doesn't break `import.meta.env` refs

/**
 * Statically returns all known environment variables in a single place to prevent issues with
 * code formatting (e.g. line breaks) potentially breaking the static replacement of runtime variables
 * during build.
 * 
 * This is necessary to adhere to their [special expression](https://import-meta-env.org/guide/getting-started/introduction.html#special-expression).
 * 
 * WARNING: If variables are not statically referenced, their links to runtime envs might silently break
 * when the App gets built and deployed.
 * 
 * @see {@link https://e-pilot.atlassian.net/browse/ER-4839}
 */
export const loadEnvs = () => {
  return {
    // compile-time variables
    BASE_URL: import.meta.env.BASE_URL,
    REACT_APP_VERSION: import.meta.env.REACT_APP_VERSION,
    // runtime variables
    REACT_APP_STAGE: import.meta.env.REACT_APP_STAGE,
    REACT_APP_JOURNEYS_API_BASE_URL: import.meta.env.REACT_APP_JOURNEYS_API_BASE_URL,
    REACT_APP_JOURNEYS_DATASOURCES_BASE_URL: import.meta.env.REACT_APP_JOURNEYS_DATASOURCES_BASE_URL,
    REACT_APP_JOURNEYS_SUBMISSION_API: import.meta.env.REACT_APP_JOURNEYS_SUBMISSION_API,
    REACT_APP_PORTAL_URL: import.meta.env.REACT_APP_PORTAL_URL,
    REACT_APP_EMBED_BASE_URL: import.meta.env.REACT_APP_EMBED_BASE_URL,
    REACT_APP_FILE_API_URL: import.meta.env.REACT_APP_FILE_API_URL,
    REACT_APP_ANALYTICS_API_BASE_URL: import.meta.env.REACT_APP_ANALYTICS_API_BASE_URL,
    REACT_APP_IMAGE_PREVIEW_API_URL: import.meta.env.REACT_APP_IMAGE_PREVIEW_API_URL,
    REACT_APP_GOOGLE_MAPS_API_URL: import.meta.env.REACT_APP_GOOGLE_MAPS_API_URL,
    REACT_APP_DESIGN_BUILDER_API_URL: import.meta.env.REACT_APP_DESIGN_BUILDER_API_URL,
    REACT_APP_IBAN_API_URL: import.meta.env.REACT_APP_IBAN_API_URL,
    REACT_APP_PRICING_API_URL: import.meta.env.REACT_APP_PRICING_API_URL,
    REACT_APP_ADDRESS_API_URL: import.meta.env.REACT_APP_ADDRESS_API_URL,
    REACT_APP_ADDRESS_SUGGESTIONS_API_URL: import.meta.env.REACT_APP_ADDRESS_SUGGESTIONS_API_URL,
    REACT_APP_PREVIOUS_PROVIDER_URL: import.meta.env.REACT_APP_PREVIOUS_PROVIDER_URL,
    REACT_APP_ENTITY_API_URL: import.meta.env.REACT_APP_ENTITY_API_URL,
    REACT_APP_CUSTOMER_PORTAL_API_URL: import.meta.env.REACT_APP_CUSTOMER_PORTAL_API_URL,
    REACT_APP_METERING_API_URL: import.meta.env.REACT_APP_METERING_API_URL,
    REACT_APP_APPS_API_URL: import.meta.env.REACT_APP_APPS_API_URL,
    REACT_APP_VALIDATION_RULES_API_BASE_URL: import.meta.env.REACT_APP_VALIDATION_RULES_API_BASE_URL
  }
}

/**
 * Since {@link loadEnvs} should result in the same on every call, this keeps track of its cached result
 * after the initial call, which is fully managed in {@link env}.
 */
let cachedEnvs: ReturnType<typeof loadEnvs>

/**
 * Returns the value of a given environment variable key.
 * 
 * NOTE: For safety, always use this method to access environment variables
 * instead of directly referencing `import.meta.env`. See {@link loadEnvs}
 * for more information.
 */
export function env<K extends keyof ReturnType<typeof loadEnvs>>(key: K) {
  if (!cachedEnvs) cachedEnvs = loadEnvs()

  return cachedEnvs[key]
}